/**
 * Client-side transport failures that are not actionable app bugs.
 *
 * Kept free of imports, like `requestCancellation.js`, so the user store can use it without pulling in
 * `errorHandler.js`, which reaches the whole store index.
 */

/**
 * ERR_NETWORK covers XHR status 0 (offline, Wi‑Fi drop, sleep/wake); ERR_CANCELED is intentional abort.
 * ECONNABORTED covers a browser-aborted request and a request timeout, which axios gives the same code.
 * The abort is noise: MOVEPRO-CRM-25 (30 events). A timeout is a real availability signal and must keep
 * reporting — MOVEPRO-CRM-E3 is 1,195 login timeouts across 62 users, still firing (CU-86d477a1g).
 *
 * The two are separated on axios's message, not on `config.timeout`. That looks like the cleaner
 * discriminator and is wrong: axios emits the bare 'timeout exceeded' precisely WHEN `config.timeout` is
 * falsy (adapters/xhr.js, `request.ontimeout`), and `ontimeout` does fire with no timeout configured, which
 * is what MOVEPRO-CRM-E3 is. Testing `!config.timeout` would classify every one of those as noise.
 *
 * Only axios's exact `onabort` message counts as noise, so any ECONNABORTED we do not recognise keeps
 * reporting. That fails safe.
 * Keep in sync with Book `isNoiseAxiosError` in apps/book/src/utilities/axiosNoise.ts.
 *
 * Reads only `code` and `message`, so it classifies the object `processError` returns the same way as
 * the raw axios error. The user store's hydration catch receives the processed object and must not
 * report or sign the operator out on this noise (CU-86d4a566a).
 */
export const ABORTED_MESSAGE = 'Request aborted';

export function isNoiseAxiosError(error) {
    if (error?.code === 'ERR_CANCELED' || error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        return true;
    }

    return error?.code === 'ECONNABORTED' && error?.message === ABORTED_MESSAGE;
}
